import type { PhotoSection } from '../shared/photo';
import { openDatabase } from './photoDraft';
import { createIdempotencyKey } from './idempotency';
export type GuestDraftItem = { file: File; key: string; caption: string; status: string };
type StoredItem = { key: string; caption: string; bytes: ArrayBuffer; name: string; type: string; lastModified: number };
const marker = 'lloyds-guest-draft-v1';
const maxAge = 24 * 60 * 60 * 1000;
let writes: Promise<void> = Promise.resolve();
const bytes = new WeakMap<File, Promise<ArrayBuffer>>();
let tabId: string | undefined;
function batchKey(section: PhotoSection) {
  if (!tabId) {
    try {
      tabId = sessionStorage.getItem('lloyds-guest-tab') || createIdempotencyKey();
      sessionStorage.setItem('lloyds-guest-tab', tabId);
    } catch { tabId = createIdempotencyKey(); }
  }
  return `guest-batch-${tabId}-${section}`;
}
export function pendingGuestDraft(): PhotoSection | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(marker) || 'null');
    return value && Date.now() - value.at < maxAge && ['celebration', 'memories'].includes(value.section) ? value.section : null;
  } catch { return null; }
}
export function markGuestDraft(section: PhotoSection) {
  try { sessionStorage.setItem(marker, JSON.stringify({ section, at: Date.now() })); } catch { /* Optional recovery. */ }
}
export function saveGuestDraft(section: PhotoSection, name: string, items: GuestDraftItem[]) {
  const pending = items.filter((item) => item.status !== 'done');
  if (pending.length) markGuestDraft(section);
  else { try { sessionStorage.removeItem(marker); } catch { /* Optional recovery. */ } }
  const operation = writes.catch(() => {}).then(async () => {
    const saved: StoredItem[] = [];
    for (const item of pending) {
      if (!bytes.has(item.file)) bytes.set(item.file, item.file.arrayBuffer());
      saved.push({ key: item.key, caption: item.caption, bytes: (await bytes.get(item.file))!,
        name: item.file.name, type: item.file.type, lastModified: item.file.lastModified });
    }
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('drafts', 'readwrite');
      const store = tx.objectStore('drafts');
      const key = batchKey(section);
      if (saved.length) store.put({ key, name, items: saved, savedAt: Date.now() });
      else store.delete(key);
      tx.oncomplete = () => resolve(); tx.onerror = tx.onabort = () => reject(tx.error);
    });
  });
  writes = operation;
  return operation;
}
export async function restoreGuestDraft(section: PhotoSection) {
  await writes.catch(() => {});
  const db = await openDatabase();
  return new Promise<{ name: string; items: GuestDraftItem[] } | null>((resolve, reject) => {
    const tx = db.transaction('drafts', 'readwrite');
    const request = tx.objectStore('drafts').get(batchKey(section));
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      try {
      const saved = request.result;
      if (!saved) { resolve(null); return; }
      if (Date.now() - saved.savedAt >= maxAge) {
        tx.objectStore('drafts').delete(batchKey(section)); resolve(null); return;
      }
      if (!Array.isArray(saved.items) || saved.items.some((item: StoredItem) => !(item.bytes instanceof ArrayBuffer) || !item.bytes.byteLength || typeof item.key !== 'string'))
        throw new Error('The saved photos could not be read.');
      resolve({ name: saved.name, items: saved.items.map((item: StoredItem) => ({
        key: item.key, caption: item.caption, status: 'ready',
        file: new File([item.bytes], item.name, { type: item.type, lastModified: item.lastModified }),
      })) });
      } catch (error) { reject(error); }
    };
  });
}
